'use client';

import { c as _c } from "react/compiler-runtime";
import type { UserPortfolioSnapshotWire } from '@morfi/backend-sdk-v2/client';
import { useCallback } from 'react'; // ARBITRARY: TanStack Query requires stable selector identities.
import { getUsdcMint } from '@/config/solana';
import { isVisibleSnapshotMarketPosition } from '@/lib/market/position-visibility';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import type { UserPortfolioPositionDisplayRow, UserPortfolioSummary } from './display-types';
import { useUserPortfolioSnapshotQuerySelect } from './query-select';
import { userPortfolioPositionDisplayRow, userPortfolioSummary } from './valuation';
export interface UserPortfolioSelectorState<T> {
  readonly data: T;
  readonly isPending: boolean;
  readonly isError: boolean;
}
export function useUserPortfolioSummary({
  userId,
  wallets
}: {
  readonly userId: string;
  readonly wallets: readonly string[];
}): UserPortfolioSelectorState<UserPortfolioSummary> & UserPortfolioSummary {
  const quoteMint = getUsdcMint(useActiveNetwork());
  const walletKey = [...new Set(wallets)].sort().join('\n');
  function selectSummary(snapshot: UserPortfolioSnapshotWire | null): UserPortfolioSummary {
    return snapshot === null ? unavailableSummary() : userPortfolioSummary(snapshot, splitWalletKey(walletKey), quoteMint);
  }
  const select = useCallback(selectSummary, [quoteMint, walletKey]); // ARBITRARY: TanStack Query requires a stable select identity for unchanged scope inputs.
  const query = useUserPortfolioSnapshotQuerySelect(userId, select);
  const data = query.data ?? unavailableSummary();
  return {
    ...data,
    data,
    isPending: query.isPending,
    isError: query.isError
  };
}
export function useUserWalletPortfolioSummary(t0) {
  const $ = _c(5);
  const {
    userId,
    wallet
  } = t0;
  let t1;
  if ($[0] !== wallet) {
    t1 = wallet.length === 0 ? [] : [wallet];
    $[0] = wallet;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== userId) {
    t2 = {
      userId,
      wallets: t1
    };
    $[2] = t1;
    $[3] = userId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useUserPortfolioSummary(t2);
}
export function useUserPortfolioPositionRows({
  userId,
  wallets
}: {
  readonly userId: string;
  readonly wallets: readonly string[];
}): UserPortfolioSelectorState<readonly UserPortfolioPositionDisplayRow[]> & {
  readonly rows: readonly UserPortfolioPositionDisplayRow[];
} {
  const walletKey = [...new Set(wallets)].sort().join('\n');
  function selectRows(snapshot: UserPortfolioSnapshotWire | null): readonly UserPortfolioPositionDisplayRow[] {
    if (snapshot === null) return [];
    const selectedWallets = new Set(walletKey.length === 0 ? snapshot.wallets : splitWalletKey(walletKey));
    return snapshot.positions.filter(position => selectedWallets.has(position.wallet)).map(position_0 => userPortfolioPositionDisplayRow(position_0, snapshot.poolSnapshotsByMarket[position_0.market]));
  }
  const select = useCallback(selectRows, [walletKey]); // ARBITRARY: TanStack Query requires a stable select identity for unchanged scope inputs.
  const query = useUserPortfolioSnapshotQuerySelect(userId, select);
  const rows = query.data ?? [];
  return {
    data: rows,
    rows,
    isPending: query.isPending,
    isError: query.isError
  };
}
export function useUserMarketPortfolioPositions(t0) {
  const $ = _c(12);
  const {
    userId,
    wallet,
    market,
    outcomeMints
  } = t0;
  let t1;
  if ($[0] !== outcomeMints) {
    t1 = [...new Set(outcomeMints ?? [])].sort().join("\n");
    $[0] = outcomeMints;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const outcomeMintKey = t1;
  let t2;
  if ($[2] !== market || $[3] !== outcomeMintKey || $[4] !== wallet) {
    t2 = snapshot => {
      if (snapshot === null || wallet.length === 0) {
        return [];
      }
      const selectedOutcomeMints = new Set(splitWalletKey(outcomeMintKey));
      return snapshot.positions.flatMap(position => {
        const matchesScope = position.wallet === wallet && (market === undefined || position.market === market) && (selectedOutcomeMints.size === 0 || selectedOutcomeMints.has(position.outcomeMint));
        return matchesScope && isVisibleSnapshotMarketPosition(position) ? [userPortfolioPositionDisplayRow(position, snapshot.poolSnapshotsByMarket[position.market])] : [];
      });
    };
    $[2] = market;
    $[3] = outcomeMintKey;
    $[4] = wallet;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const select = t2;
  const query = useUserPortfolioSnapshotQuerySelect(userId, select);
  let t3;
  if ($[6] !== query.data) {
    t3 = query.data ?? [];
    $[6] = query.data;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const rows = t3;
  let t4;
  if ($[8] !== query.isError || $[9] !== query.isPending || $[10] !== rows) {
    t4 = {
      data: rows,
      rows,
      isPending: query.isPending,
      isError: query.isError
    };
    $[8] = query.isError;
    $[9] = query.isPending;
    $[10] = rows;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function unavailableSummary(): UserPortfolioSummary {
  return {
    cashD6: null,
    holdingsD6: null,
    totalD6: null,
    positionCount: null,
    unvaluedPositions: 0,
    unavailableReason: null
  };
}
function splitWalletKey(walletKey: string): readonly string[] {
  return walletKey.length === 0 ? [] : walletKey.split('\n');
}